import React from 'react';
import { Segment, Header, Icon, List, Table, Message, Divider, Image } from 'semantic-ui-react';

interface DocumentationViewerProps {
  content: string;
}

const DocumentationViewer: React.FC<DocumentationViewerProps> = ({ content }) => {
  // Função para renderizar o conteúdo formatado
  const renderContent = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let currentList: string[] = [];
    let currentTable: string[][] = [];
    let inTable = false;
    let inList = false;
    let listType: 'ordered' | 'unordered' | null = null;

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      // Tabela
      if (trimmed.startsWith('|')) {
        if (!inTable) {
          inTable = true;
          currentTable = [];
        }
        const cells = trimmed.split('|').map(cell => cell.trim()).filter(cell => cell);
        if (cells.length > 0 && !cells[0].includes('---')) {
          currentTable.push(cells);
        }
        return;
      } else if (inTable && currentTable.length > 0) {
        elements.push(
          <Table key={`table-${index}`} celled compact>
            <Table.Header>
              <Table.Row>
                {currentTable[0].map((header, i) => (
                  <Table.HeaderCell key={i}>{header}</Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {currentTable.slice(1).map((row, i) => (
                <Table.Row key={i}>
                  {row.map((cell, j) => (
                    <Table.Cell key={j}>{renderInlineFormatting(cell)}</Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        );
        currentTable = [];
        inTable = false;
      }

      // Lista
      if (trimmed.match(/^[-*]\s/) || trimmed.match(/^\d+\.\s/)) {
        if (!inList) {
          inList = true;
          listType = trimmed.match(/^\d+\.\s/) ? 'ordered' : 'unordered';
        }
        const item = trimmed.replace(/^[-*]\s/, '').replace(/^\d+\.\s/, '');
        currentList.push(item);
        return;
      } else if (inList && currentList.length > 0) {
        if (listType === 'ordered') {
          elements.push(
            <List ordered key={`list-${index}`} style={{ marginBottom: '16px' }}>
              {currentList.map((item, i) => (
                <List.Item key={i}>{renderInlineFormatting(item)}</List.Item>
              ))}
            </List>
          );
        } else {
          elements.push(
            <List bulleted key={`list-${index}`} style={{ marginBottom: '16px' }}>
              {currentList.map((item, i) => (
                <List.Item key={i}>{renderInlineFormatting(item)}</List.Item>
              ))}
            </List>
          );
        }
        currentList = [];
        inList = false;
      }

      // Títulos
      if (trimmed.startsWith('# ')) {
        elements.push(
          <Header as="h1" key={`h1-${index}`} style={{ marginTop: '20px', marginBottom: '16px' }}>
            {trimmed.replace('# ', '')}
          </Header>
        );
        return;
      }
      if (trimmed.startsWith('## ')) {
        elements.push(
          <Header as="h2" key={`h2-${index}`} style={{ marginTop: '20px', marginBottom: '12px' }}>
            {trimmed.replace('## ', '')}
          </Header>
        );
        return;
      }
      if (trimmed.startsWith('### ')) {
        elements.push(
          <Header as="h3" key={`h3-${index}`} style={{ marginTop: '16px', marginBottom: '10px' }}>
            {trimmed.replace('### ', '')}
          </Header>
        );
        return;
      }

      // Blockquote
      if (trimmed.startsWith('>')) {
        const quoteText = trimmed.replace(/^>\s*/, '');
        elements.push(
          <Message info key={`quote-${index}`} style={{ marginBottom: '16px' }}>
            {renderInlineFormatting(quoteText)}
          </Message>
        );
        return;
      }

      // Divisor
      if (trimmed === '---' || trimmed === '***') {
        elements.push(<Divider key={`divider-${index}`} />);
        return;
      }

      // Imagem - formato markdown ![alt](image.png) ou ![alt](/images/documentation/image.png)
      const imageMatch = trimmed.match(/!\[([^\]]*)\]\(([^)]+)\)/);
      if (imageMatch) {
        const altText = imageMatch[1] || 'Imagem da documentação';
        let imagePath = imageMatch[2];
        
        // Se o caminho não começar com http ou /, assumir que está em /images/documentation/
        if (!imagePath.startsWith('http') && !imagePath.startsWith('/')) {
          imagePath = `/images/documentation/${imagePath}`;
        }
        
        elements.push(
          <div key={`img-${index}`} style={{ margin: '20px 0', textAlign: 'center' }}>
            <Image
              src={imagePath}
              alt={altText}
              rounded
              bordered
              style={{
                maxWidth: '100%',
                height: 'auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              onError={(e: any) => {
                e.target.style.display = 'none';
                const errorDiv = document.createElement('div');
                errorDiv.textContent = `Imagem não encontrada: ${altText}`;
                errorDiv.style.cssText = 'padding: 20px; text-align: center; color: #999; font-style: italic;';
                e.target.parentElement?.appendChild(errorDiv);
              }}
            />
            {altText && altText !== 'Imagem da documentação' && (
              <p style={{ marginTop: '8px', fontSize: '0.9em', color: '#666', fontStyle: 'italic' }}>
                {altText}
              </p>
            )}
          </div>
        );
        return;
      }

      // Parágrafo normal
      if (trimmed.length > 0) {
        elements.push(
          <p key={`p-${index}`} style={{ marginBottom: '12px', lineHeight: '1.6' }}>
            {renderInlineFormatting(trimmed)}
          </p>
        );
      }
    });

    // Renderizar listas pendentes
    if (inList && currentList.length > 0 && listType) {
      if (listType === 'ordered') {
        elements.push(
          <List ordered key="list-final">
            {currentList.map((item, i) => (
              <List.Item key={i}>{renderInlineFormatting(item)}</List.Item>
            ))}
          </List>
        );
      } else {
        elements.push(
          <List bulleted key="list-final">
            {currentList.map((item, i) => (
              <List.Item key={i}>{renderInlineFormatting(item)}</List.Item>
            ))}
          </List>
        );
      }
    }

    return elements;
  };

  // Função para renderizar formatação inline (negrito, itálico, código)
  const renderInlineFormatting = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let currentIndex = 0;
    let key = 0;

    // Negrito **texto**
    const boldRegex = /\*\*(.+?)\*\*/g;
    // Itálico *texto*
    const italicRegex = /\*(.+?)\*/g;
    // Código inline `código`
    const codeRegex = /`(.+?)`/g;

    const matches: Array<{ start: number; end: number; type: 'bold' | 'italic' | 'code'; content: string }> = [];

    let match: RegExpExecArray | null;
    while ((match = boldRegex.exec(text)) !== null) {
      matches.push({ start: match.index, end: match.index + match[0].length, type: 'bold', content: match[1] });
    }
    while ((match = italicRegex.exec(text)) !== null) {
      if (!matches.some(m => m.start <= match!.index && m.end >= match!.index)) {
        matches.push({ start: match.index, end: match.index + match[0].length, type: 'italic', content: match[1] });
      }
    }
    while ((match = codeRegex.exec(text)) !== null) {
      if (!matches.some(m => m.start <= match!.index && m.end >= match!.index)) {
        matches.push({ start: match.index, end: match.index + match[0].length, type: 'code', content: match[1] });
      }
    }

    matches.sort((a, b) => a.start - b.start);

    matches.forEach((match, index) => {
      if (match.start > currentIndex) {
        parts.push(text.substring(currentIndex, match.start));
      }

      if (match.type === 'bold') {
        parts.push(<strong key={`bold-${key++}`}>{match.content}</strong>);
      } else if (match.type === 'italic') {
        parts.push(<em key={`italic-${key++}`}>{match.content}</em>);
      } else if (match.type === 'code') {
        parts.push(
          <code key={`code-${key++}`} style={{ background: '#f4f4f4', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace' }}>
            {match.content}
          </code>
        );
      }

      currentIndex = match.end;
    });

    if (currentIndex < text.length) {
      parts.push(text.substring(currentIndex));
    }

    return parts.length > 0 ? <>{parts}</> : text;
  };

  return (
    <Segment style={{ padding: '24px', background: 'white' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {renderContent(content)}
      </div>
    </Segment>
  );
};

export default DocumentationViewer;

